import React, { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { Toaster } from './components/ui/sonner';
import AssistantWidget from './components/AssistantWidget';
import './App.css';

// Public pages
const AccueilSectionFrance = lazy(() => import('./pages/AccueilSectionFrance'));
const AProposPage = lazy(() => import('./pages/AProposPage'));
const OrganisationPage = lazy(() => import('./pages/OrganisationPage'));
const EquipePage = lazy(() => import('./pages/EquipePage'));
const DiasporaPage = lazy(() => import('./pages/DiasporaPage'));
const ActualitesPage = lazy(() => import('./pages/ActualitesPage'));
const EvenementsPage = lazy(() => import('./pages/EvenementsPage'));
const MediasPage = lazy(() => import('./pages/MediasPage'));
const BoutiquePage = lazy(() => import('./pages/BoutiquePage'));
const CoordinationsPage = lazy(() => import('./pages/CoordinationsPage'));
const ContactPage = lazy(() => import('./pages/ContactPage'));
const LoginPage = lazy(() => import('./pages/LoginPage'));
const RegisterInvitePage = lazy(() => import('./pages/RegisterInvitePage'));

// Member pages
const DashboardPage = lazy(() => import('./pages/DashboardPage'));
const ProfilePage = lazy(() => import('./pages/ProfilePage'));
const MessagesPage = lazy(() => import('./pages/MessagesPage'));
const DocumentsPage = lazy(() => import('./pages/DocumentsPage'));
const CotisationsPage = lazy(() => import('./pages/CotisationsPage'));
const VotesPage = lazy(() => import('./pages/VotesPage'));
const SurveysPage = lazy(() => import('./pages/SurveysPage'));
const MemberEventsPage = lazy(() => import('./pages/MemberEventsPage'));
const ComptabilitePage = lazy(() => import('./pages/ComptabilitePage'));
const ElectionsPage = lazy(() => import('./pages/ElectionsPage'));
const SanctionsPage = lazy(() => import('./pages/SanctionsPage'));
const ForumPage = lazy(() => import('./pages/ForumPage'));
const GuidePage = lazy(() => import('./pages/GuidePage'));

// Admin pages
const AdminPage = lazy(() => import('./pages/AdminPage'));
const CoordinationAdminPage = lazy(() => import('./pages/CoordinationAdminPage'));

const Loading = () => (
  <div className="min-h-screen flex items-center justify-center bg-[#0B1120]">
    <div className="w-12 h-12 border-4 border-[#2563EB] border-t-transparent rounded-full animate-spin"></div>
  </div>
);

const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  if (loading) return <Loading />;
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  return children;
};

const AdminRoute = ({ children }) => {
  const { isAdmin, loading } = useAuth();
  if (loading) return <Loading />;
  if (!isAdmin) return <Navigate to="/dashboard" replace />;
  return children;
};

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Suspense fallback={<Loading />}>
          <Routes>
            {/* Public — Site Vitrine */}
            <Route path="/" element={<AccueilSectionFrance />} />
            <Route path="/a-propos" element={<AProposPage />} />
            <Route path="/organisation" element={<OrganisationPage />} />
            <Route path="/equipe" element={<EquipePage />} />
            <Route path="/diaspora" element={<DiasporaPage />} />
            <Route path="/actualites" element={<ActualitesPage />} />
            <Route path="/evenements" element={<EvenementsPage />} />
            <Route path="/medias" element={<MediasPage />} />
            <Route path="/boutique" element={<BoutiquePage />} />
            <Route path="/coordinations" element={<CoordinationsPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/inscription/:token" element={<RegisterInvitePage />} />

            {/* Espace Membre */}
            <Route path="/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
            <Route path="/profile" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
            <Route path="/messages" element={<ProtectedRoute><MessagesPage /></ProtectedRoute>} />
            <Route path="/documents" element={<ProtectedRoute><DocumentsPage /></ProtectedRoute>} />
            <Route path="/cotisations" element={<ProtectedRoute><CotisationsPage /></ProtectedRoute>} />
            <Route path="/cotisations/success" element={<ProtectedRoute><CotisationsPage /></ProtectedRoute>} />
            <Route path="/votes" element={<ProtectedRoute><VotesPage /></ProtectedRoute>} />
            <Route path="/surveys" element={<ProtectedRoute><SurveysPage /></ProtectedRoute>} />
            <Route path="/member-events" element={<ProtectedRoute><MemberEventsPage /></ProtectedRoute>} />
            <Route path="/comptabilite" element={<ProtectedRoute><ComptabilitePage /></ProtectedRoute>} />
            <Route path="/elections" element={<ProtectedRoute><ElectionsPage /></ProtectedRoute>} />
            <Route path="/sanctions" element={<ProtectedRoute><SanctionsPage /></ProtectedRoute>} />
            <Route path="/forum" element={<ProtectedRoute><ForumPage /></ProtectedRoute>} />
            <Route path="/guide" element={<ProtectedRoute><GuidePage /></ProtectedRoute>} />
            <Route path="/coordination" element={<ProtectedRoute><CoordinationAdminPage /></ProtectedRoute>} />

            {/* Admin */}
            <Route path="/admin" element={<AdminRoute><AdminPage /></AdminRoute>} />
            <Route path="/admin/*" element={<AdminRoute><AdminPage /></AdminRoute>} />

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Suspense>
        <Toaster position="top-right" />
        <PublicAssistantGuard />
      </AuthProvider>
    </BrowserRouter>
  );
}

const PublicAssistantGuard = () => {
  const location = useLocation();
  const isMemberArea = location.pathname.startsWith('/dashboard') || 
    location.pathname.startsWith('/admin') || 
    location.pathname.startsWith('/profile') || 
    location.pathname.startsWith('/messages') || 
    location.pathname.startsWith('/documents') || 
    location.pathname.startsWith('/cotisations') || 
    location.pathname.startsWith('/votes') || 
    location.pathname.startsWith('/surveys') || 
    location.pathname.startsWith('/member-events') || 
    location.pathname.startsWith('/comptabilite') || 
    location.pathname.startsWith('/elections') || 
    location.pathname.startsWith('/sanctions') || 
    location.pathname.startsWith('/forum') || 
    location.pathname.startsWith('/guide') || 
    location.pathname.startsWith('/coordination');
  if (isMemberArea) return null;
  return <AssistantWidget />;
};

export default App;
